import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Clock,
  DollarSign,
  ShieldCheck,
  ShieldAlert,
  GitCommit,
  GitPullRequest,
  Rocket,
  FlaskConical,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  Package,
  AlertCircle,
  Timer,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ServiceOrderDto, PaymentDto } from "@shared/types/ServiceOrderDto";
import type { WorkOrderDto } from "@shared/types/WorkOrderDto";
import { useBilling } from "@/providers/BillingProvider";
import { useStripeInvoices, type PollEntry } from "@/providers/StripeInvoiceProvider";
import { useWorkOrders } from "@/providers/WorkOrdersProvider";

interface BillingSectionProps {
  serviceOrder: ServiceOrderDto;
}

function getDevStatusConfig(status: string) {
  switch (status) {
    case "finished":
      return { label: "Finished", className: "bg-emerald-400/20 text-emerald-400" };
    case "in-progress":
      return { label: "In Progress", className: "bg-blue-400/20 text-blue-400" };
    default:
      return { label: status, className: "bg-muted text-muted-foreground" };
  }
}

function getPaymentStatusConfig(status: string) {
  switch (status) {
    case "paid":
      return { label: "Paid", className: "bg-emerald-400/20 text-emerald-400" };
    case "unpaid":
      return { label: "Unpaid", className: "bg-red-400/20 text-red-400" };
    case "pending":
      return { label: "Pending", className: "bg-amber-400/20 text-amber-400" };
    case "failed":
      return { label: "Failed", className: "bg-red-400/20 text-red-400" };
    default:
      return { label: status, className: "bg-muted text-muted-foreground" };
  }
}

function PaymentStatusDialog({
  poll,
  onDismiss,
}: {
  poll: PollEntry;
  onDismiss: () => void;
}) {
  const elapsed = Math.floor((Date.now() - poll.startedAt) / 1000);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (poll.state !== "polling") return;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [poll.state]);

  const elapsedSeconds = poll.state === "polling"
    ? Math.floor((Date.now() - poll.startedAt) / 1000) + tick * 0
    : elapsed;

  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;

  const isDeposit = poll.isDeposit === true;

  return (
    <Dialog open onOpenChange={() => onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {poll.state === "polling" && (
              <>
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Waiting for {isDeposit ? "Deposit " : ""}Payment
              </>
            )}
            {poll.state === "paid" && (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                {isDeposit ? "Deposit " : ""}Payment Confirmed
              </>
            )}
            {poll.state === "timeout" && (
              <>
                <Timer className="h-5 w-5 text-amber-400" />
                Payment Check Timed Out
              </>
            )}
            {poll.state === "error" && (
              <>
                <AlertCircle className="h-5 w-5 text-red-400" />
                Payment Check Failed
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {poll.state === "polling" && (
              <>Checking payment status for {isDeposit ? "deposit " : ""}invoice #{poll.invoiceId}. This will automatically update once payment is confirmed.</>
            )}
            {poll.state === "paid" && (
              <>{isDeposit ? "Deposit invoice" : "Invoice"} #{poll.invoiceId} has been successfully paid{poll.result?.paidAt ? ` on ${new Date(poll.result.paidAt).toLocaleDateString()}` : ""}.</>
            )}
            {poll.state === "timeout" && (
              <>Payment confirmation for {isDeposit ? "deposit " : ""}invoice #{poll.invoiceId} did not arrive within 2 minutes. The payment may still be processing. Please check back later.</>
            )}
            {poll.state === "error" && (
              <>{poll.error || "An error occurred while checking the payment status. Please try again."}</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isDeposit && (
            <div className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Project Deposit</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {poll.state === "paid" 
                  ? "Your project is now verified and work can begin."
                  : "Required to start your project and unlock development access."}
              </p>
            </div>
          )}

          {poll.state === "polling" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <Badge className="bg-blue-400/20 text-blue-400">Checking...</Badge>
              </div>
              {isDeposit && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <Badge variant="outline" className="text-primary border-primary/30">Deposit</Badge>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Elapsed</span>
                <span className="font-mono text-sm">
                  {minutes > 0 ? `${minutes}m ` : ""}{seconds}s
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-primary h-full rounded-full transition-all duration-1000"
                  style={{
                    width: `${Math.min(((Date.now() - poll.startedAt) / 120000) * 100, 100)}%`,
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Polling every 5 seconds. Max wait: 2 minutes.
              </p>
            </div>
          )}

          {poll.state === "paid" && poll.result && (
            <div className="space-y-3">
              {isDeposit && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <Badge variant="outline" className="text-primary border-primary/30">Deposit</Badge>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-semibold">
                  ${poll.result.amount?.toLocaleString()} {poll.result.currency}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <Badge className="bg-emerald-400/20 text-emerald-400">Paid</Badge>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {poll.state === "polling" ? (
            <Button variant="outline" onClick={onDismiss}>
              Close & Continue in Background
            </Button>
          ) : (
            <Button onClick={onDismiss}>
              {poll.state === "paid" ? "Done" : "Close"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrderCard({
  workOrder,
  payment,
  paymentModel,
  onApprove,
  isApproving,
}: {
  workOrder: WorkOrderDto;
  payment: PaymentDto | undefined;
  paymentModel: string;
  onApprove: (invoiceId: string) => void;
  isApproving: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const devStatus = getDevStatusConfig(workOrder.status);
  const paymentStatus = payment ? getPaymentStatusConfig(payment.status) : null;

  const canApprove =
    payment &&
    payment.status === "unpaid" &&
    workOrder.status === "finished";

  const commitCount = workOrder.workUnits?.reduce((s, wu) => s + (wu.commits?.length || 0), 0) ?? 0;
  const prCount = workOrder.workUnits?.reduce((s, wu) => s + (wu.pullRequests?.length || 0), 0) ?? 0;
  const deployCount = workOrder.workUnits?.reduce((s, wu) => s + (wu.deployments?.length || 0), 0) ?? 0;
  const testCount = workOrder.workUnits?.reduce((s, wu) => s + (wu.tests?.length || 0), 0) ?? 0;

  return (
    <Card
      className={cn(
        "glass-card border-gradient transition-all",
        canApprove && "border-primary/40"
      )}
    >
      <CardContent className="p-0">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-5 text-left"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-primary shrink-0" />
                <h3 className="font-semibold truncate">{workOrder.project.name}</h3>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {workOrder.project.scope}
              </p>

              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={devStatus.className}>{devStatus.label}</Badge>
                {paymentStatus && (
                  <Badge className={paymentStatus.className}>{paymentStatus.label}</Badge>
                )}
              </div>

              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                {commitCount > 0 && (
                  <span className="flex items-center gap-1">
                    <GitCommit className="h-3 w-3" /> {commitCount}
                  </span>
                )}
                {prCount > 0 && (
                  <span className="flex items-center gap-1">
                    <GitPullRequest className="h-3 w-3" /> {prCount}
                  </span>
                )}
                {deployCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Rocket className="h-3 w-3" /> {deployCount}
                  </span>
                )}
                {testCount > 0 && (
                  <span className="flex items-center gap-1">
                    <FlaskConical className="h-3 w-3" /> {testCount}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
              {payment && (
                <p className="text-lg font-bold">
                  {payment.currency} ${payment.amount.toLocaleString()}
                </p>
              )}
              {expanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-border/30 px-5 pb-5">
            {payment && paymentModel === "hourly" && (
              <div className="mt-4 p-4 rounded-lg bg-card/50 border border-border/30">
                <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  Hourly Breakdown
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Hours Billed</p>
                    <p className="font-semibold">{payment.billedHours}h</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Hourly Rate</p>
                    <p className="font-semibold">
                      ${payment.hourlyRate?.toFixed(2) ?? "60.00"} {payment.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Discount</p>
                    <p className="font-semibold">
                      {payment.discount > 0 ? `${payment.discount}%` : "None"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total</p>
                    <p className="font-semibold text-primary">
                      ${payment.amount.toLocaleString()} {payment.currency}
                    </p>
                  </div>
                </div>
                {payment.discount > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Subtotal: ${(payment.billedHours * (payment.hourlyRate ?? 60)).toLocaleString()} &rarr;
                    After {payment.discount}% discount: ${payment.amount.toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {payment && paymentModel === "milestone" && (
              <div className="mt-4 p-4 rounded-lg bg-card/50 border border-border/30">
                <h4 className="text-sm font-semibold mb-2">Milestone Payment</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Amount</p>
                    <p className="font-semibold">
                      ${payment.amount.toLocaleString()} {payment.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-semibold">
                      {new Date(payment.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {payment && paymentModel === "fixed" && (
              <div className="mt-4 p-4 rounded-lg bg-card/50 border border-border/30">
                <h4 className="text-sm font-semibold mb-2">Fixed Price Deliverable</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Deliverable Amount</p>
                    <p className="font-semibold">
                      ${payment.amount.toLocaleString()} {payment.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-semibold">
                      {new Date(payment.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Payment is released upon delivery approval.
                </p>
              </div>
            )}

            {workOrder.workUnits?.length > 0 && (() => {
              const allCommits = workOrder.workUnits.flatMap((wu) => wu.commits);
              const allPRs = workOrder.workUnits.flatMap((wu) => wu.pullRequests);
              const allDeployments = workOrder.workUnits.flatMap((wu) => wu.deployments);
              const allTests = workOrder.workUnits.flatMap((wu) => wu.tests);
              return (
              <div className="mt-4 space-y-3">
                {allCommits.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <GitCommit className="h-3.5 w-3.5" />
                      Recent Commits
                    </h4>
                    <div className="space-y-1.5">
                      {allCommits.slice(0, 3).map((commit) => (
                        <a
                          key={commit.shasum}
                          href={commit.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm p-2 rounded hover:bg-accent/50 transition-colors"
                        >
                          <code className="text-xs text-muted-foreground">
                            {commit.shasum.substring(0, 7)}
                          </code>
                          <span className="truncate">{commit.commitMessage}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {allPRs.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <GitPullRequest className="h-3.5 w-3.5" />
                      Pull Requests
                    </h4>
                    <div className="space-y-1.5">
                      {allPRs.slice(0, 3).map((pr, i) => (
                        <a
                          key={i}
                          href={pr.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm p-2 rounded hover:bg-accent/50 transition-colors"
                        >
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] shrink-0",
                              pr.mergeStatus === "MERGED" && "border-purple-400/50 text-purple-400",
                              pr.mergeStatus === "OPEN" && "border-emerald-400/50 text-emerald-400",
                              pr.mergeStatus === "CLOSED" && "border-red-400/50 text-red-400"
                            )}
                          >
                            {pr.mergeStatus}
                          </Badge>
                          <span className="truncate">{pr.title}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {allDeployments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Rocket className="h-3.5 w-3.5" />
                      Deployments
                    </h4>
                    <div className="space-y-1.5">
                      {allDeployments.map((dep, i) => (
                        <a
                          key={i}
                          href={dep.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm p-2 rounded hover:bg-accent/50 transition-colors"
                        >
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] shrink-0",
                              dep.state === "PASSED" && "border-emerald-400/50 text-emerald-400",
                              dep.state === "FAILED" && "border-red-400/50 text-red-400",
                              dep.state === "BUILDING" && "border-amber-400/50 text-amber-400"
                            )}
                          >
                            {dep.state}
                          </Badge>
                          <span className="truncate">{dep.name}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {allTests.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <FlaskConical className="h-3.5 w-3.5" />
                      Tests
                    </h4>
                    <div className="space-y-1.5">
                      {allTests.map((test, i) => (
                        <a
                          key={i}
                          href={test.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm p-2 rounded hover:bg-accent/50 transition-colors"
                        >
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] shrink-0",
                              test.state === "PASSED" && "border-emerald-400/50 text-emerald-400",
                              test.state === "FAILED" && "border-red-400/50 text-red-400"
                            )}
                          >
                            {test.state}
                          </Badge>
                          <span className="truncate">{test.testName}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })()}

            {canApprove && (
              <div className="mt-4 pt-4 border-t border-border/30">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove(payment.invoiceId);
                  }}
                  disabled={isApproving}
                  className="w-full"
                >
                  {isApproving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <DollarSign className="h-4 w-4 mr-2" />
                  )}
                  Approve & Pay
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function BillingSection({ serviceOrder }: BillingSectionProps) {
  const { deposit, paymentModel, payments } = serviceOrder;
  const { payDeposit, isProcessing } = useBilling();
  const {
    downloadInvoice,
    isDownloading,
    pollPaymentStatus,
    dismissPoll,
    activePolls,
  } = useStripeInvoices();
  const { getWorkOrderByServiceOrder, isLoadingWorkOrders } = useWorkOrders();
  const [workOrders, setWorkOrders] = useState<WorkOrderDto[]>([]);
  const [visiblePollId, setVisiblePollId] = useState<string | null>(null);

  useEffect(() => {
    getWorkOrderByServiceOrder(serviceOrder.id)
      .then((wo) => setWorkOrders(wo ? [wo] : []))
      .catch(() => {});
  }, [serviceOrder.id, getWorkOrderByServiceOrder]);

  const totalPaid = payments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.amount, 0);
  const depositPaid = deposit.status === "paid" ? deposit.amount : 0;
  const totalSpend = totalPaid + depositPaid;
  const paymentVerified = deposit.status === "paid";

  const handlePayDeposit = () => {
    if (deposit.invoiceId) {
      payDeposit(serviceOrder.id, deposit.invoiceId);
    }
  };

  const handleApprove = (invoiceId: string, isDeposit?: boolean) => {
    downloadInvoice(invoiceId);
    pollPaymentStatus(invoiceId, isDeposit);
    setVisiblePollId(invoiceId);
  };

  const handleDismissPoll = (invoiceId: string) => {
    const poll = activePolls.find((p) => p.invoiceId === invoiceId);
    if (poll && poll.state !== "polling") {
      dismissPoll(invoiceId);
    }
    setVisiblePollId(null);
  };

  const showDepositPayButton =
    deposit.status === "unpaid" || deposit.status === "failed";

  const paidPayments = payments.filter((p) => p.status === "paid");

  const getPaymentForWorkOrder = (workOrderId: string): PaymentDto | undefined => {
    return payments.find((p) => p.workOrderId === workOrderId);
  };

  const visiblePoll = visiblePollId
    ? activePolls.find((p) => p.invoiceId === visiblePollId)
    : null;

  return (
    <div className="space-y-6">
      {visiblePoll && (
        <PaymentStatusDialog
          poll={visiblePoll}
          onDismiss={() => handleDismissPoll(visiblePoll.invoiceId)}
        />
      )}

      <Card
        className={cn(
          "glass-card",
          paymentVerified ? "border-emerald-400/30" : "border-amber-400/30"
        )}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {paymentVerified ? (
                <ShieldCheck className="h-6 w-6 text-emerald-400" />
              ) : (
                <ShieldAlert className="h-6 w-6 text-amber-400" />
              )}
              <div>
                <p className="font-medium">Payment Status</p>
                <p
                  className={cn(
                    "text-sm font-semibold",
                    paymentVerified ? "text-emerald-400" : "text-amber-400"
                  )}
                >
                  {paymentVerified ? "Verified" : "Deposit Required"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Spent</p>
                <p className="text-lg font-bold">
                  ${totalSpend.toLocaleString()} {deposit.currency}
                </p>
              </div>
              {!paymentVerified && deposit.invoiceId && (
                <Button
                  onClick={() => handleApprove(deposit.invoiceId!, true)}
                  disabled={isProcessing || !!isDownloading}
                  variant="default"
                  size="sm"
                >
                  {isProcessing || isDownloading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <DollarSign className="h-4 w-4 mr-2" />
                  )}
                  Pay Deposit
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card
        className={cn(
          "glass-card border-gradient",
          deposit.status === "paid"
            ? "border-emerald-400/30"
            : "border-amber-400/30"
        )}
      >
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Deposit</span>
            <Badge
              variant={deposit.status === "paid" ? "default" : "secondary"}
            >
              {deposit.status.toUpperCase()}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold">
                {deposit.currency} ${deposit.amount.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {deposit.status === "paid"
                  ? `Paid on ${new Date(deposit.date).toLocaleDateString()}`
                  : `Due ${new Date(deposit.date).toLocaleDateString()}`}
              </p>
            </div>
            {showDepositPayButton && (
              <Button onClick={handlePayDeposit} disabled={isProcessing}>
                <DollarSign className="h-4 w-4 mr-2" />
                Pay Now
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          Billable Work Orders
          {paymentModel === "hourly" && (
            <Badge variant="outline" className="ml-2 text-xs">Hourly</Badge>
          )}
          {paymentModel === "milestone" && (
            <Badge variant="outline" className="ml-2 text-xs">Milestone</Badge>
          )}
          {paymentModel === "fixed" && (
            <Badge variant="outline" className="ml-2 text-xs">Fixed</Badge>
          )}
        </h2>

        {isLoadingWorkOrders ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : workOrders.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="p-8 text-center">
              <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                No billable work orders yet. Work orders will appear here once development begins.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {workOrders.map((wo) => (
              <WorkOrderCard
                key={wo.id}
                workOrder={wo}
                payment={getPaymentForWorkOrder(wo.id)}
                paymentModel={paymentModel}
                onApprove={handleApprove}
                isApproving={isDownloading === wo.invoiceId}
              />
            ))}
          </div>
        )}
      </div>

      {activePolls.length > 0 && !visiblePollId && (
        <Card className="glass-card border-blue-400/30">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
              Payment Status Tracking
            </h3>
            <div className="space-y-2">
              {activePolls.map((poll) => (
                <button
                  key={poll.invoiceId}
                  onClick={() => setVisiblePollId(poll.invoiceId)}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-card/50 border border-border/30 hover:bg-accent/50 transition-colors text-left"
                >
                  <span className="text-sm">Invoice #{poll.invoiceId}</span>
                  <Badge
                    className={cn(
                      poll.state === "polling" && "bg-blue-400/20 text-blue-400",
                      poll.state === "paid" && "bg-emerald-400/20 text-emerald-400",
                      poll.state === "timeout" && "bg-amber-400/20 text-amber-400",
                      poll.state === "error" && "bg-red-400/20 text-red-400"
                    )}
                  >
                    {poll.state === "polling" ? "Checking..." : poll.state}
                  </Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {paidPayments.length > 0 && (
        <Card className="glass-card border-gradient">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {paidPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-card/50 border border-emerald-400/30"
                >
                  <div>
                    <p className="font-medium">Invoice #{payment.invoiceId}</p>
                    {paymentModel === "hourly" ? (
                      <p className="text-sm text-muted-foreground">
                        {payment.billedHours}h × ${payment.hourlyRate ?? 60}/hr
                        {payment.discount > 0 && ` (${payment.discount}% off)`}
                        {" "}&bull; ${payment.amount.toLocaleString()} {payment.currency}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        ${payment.amount.toLocaleString()} {payment.currency}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Paid: {new Date(payment.date).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge className="bg-emerald-400/20 text-emerald-400">
                    Paid
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
